import React, {
  useState, useMemo, useRef, useEffect,
} from 'react'

const LOCATION_SUGGESTIONS = [
  'Los Angeles, CA, USA',
  'New York, NY, USA',
  'San Francisco, CA, USA',
  'Boulder, CO, USA',
  'Austin, TX, USA',
  'Portland, OR, USA',
  'Seattle, WA, USA',
  'Denver, CO, USA',
  'Chicago, IL, USA',
  'Miami, FL, USA',
  'Nashville, TN, USA',
  'Atlanta, GA, USA',
  'Phoenix, AZ, USA',
  'San Diego, CA, USA',
  'Asheville, NC, USA',
  'Sedona, AZ, USA',
  'Maui, HI, USA',
  'Toronto, ON, Canada',
  'Vancouver, BC, Canada',
  'London, United Kingdom',
  'Berlin, Germany',
  'Amsterdam, Netherlands',
  'Paris, France',
  'Barcelona, Spain',
  'Melbourne, Australia',
  'Sydney, Australia',
  'Bali, Indonesia',
  'Mexico City, Mexico',
  'Tulum, Mexico',
  'Lisbon, Portugal',
  'Costa Rica',
  'Tokyo, Japan',
  'Mumbai, India',
  'Cape Town, South Africa',
  'Rio de Janeiro, Brazil',
  'Buenos Aires, Argentina',
]

interface LocationAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onSelect: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  maxLength?: number
  /** HTML id attribute for the input — enables label[htmlFor] association */
  id?: string
}

function LocationAutocomplete ({
  value,
  onChange,
  onSelect,
  placeholder = '',
  ariaLabel = '',
  maxLength = 128,
  id,
}: LocationAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (value && value.length > 0) {
      return LOCATION_SUGGESTIONS.filter(
        (s) => s.toLowerCase().includes(value.toLowerCase()),
      ).slice(0, 8)
    }
    return LOCATION_SUGGESTIONS.slice(0, 8)
  }, [value])

  useEffect(() => {
    function handleClickOutside (event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filtered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1))
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault()
      onSelect(filtered[highlightIndex])
      setShowSuggestions(false)
      setHighlightIndex(-1)
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
      setHighlightIndex(-1)
    }
  }

  return (
    <div className="location-autocomplete" ref={wrapperRef}>
      <input
        id={id}
        type="text"
        className="location-autocomplete__input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setShowSuggestions(true)
          setHighlightIndex(-1)
        }}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
      />
      {showSuggestions && filtered.length > 0 ? (
        <ul className="location-autocomplete__suggestions">
          {filtered.map((suggestion, idx) => (
            <li key={suggestion}>
              <button
                type="button"
                className={`location-autocomplete__suggestion${
                  idx === highlightIndex
                    ? ' location-autocomplete__suggestion--active'
                    : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  onSelect(suggestion)
                  setShowSuggestions(false)
                }}
                onMouseEnter={() => setHighlightIndex(idx)}
              >
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default LocationAutocomplete
